import React, { useContext, useEffect, useState } from 'react';
import { CButton, CCol, CRow } from '@coreui/react';
import { AIInsightsDetailContext } from '../Popup/AIInsightsTemplate';
import StepCardHeader from '../Components/StepCardHeaders';
import ViewScheduleCard from '../Components/ViewScheduleCard';
import CIcon from '@coreui/icons-react';
import { setFlexibleMultiModal } from '../../../../../../actions/common';
import { API_AI_INSIGHTS_TEMPLATE_SCHEDULES, API_AI_INSIGHTS_TEMPLATES_BY_ACCOUNT_CREATE, COMPONENT_NAME } from '../../../../../../constants';
import { useDispatch, useSelector } from 'react-redux';
import { toastError } from '../../../../../../utils';
import { callTokenApi } from '../../../../../../apiCaller';
import { toast } from 'react-toastify';
import { ConfirmRemovePopup, ConfirmSaveChange } from '../../../../../general/popup';
import CenterSpinner from '../../../../../general/Loadings/CenterSpinner';
import { fetchSavedReportsRequest } from '../../../../../../actions/subscriber';

const Schedule = () => {
    const {
        activeStep,
        setActiveStep,
        stepsData,
        setViewTemplates,
        handleSetUnsavedChanges,
        clearUnsavedChangesFlag,
    } = useContext(AIInsightsDetailContext);
    const dispatch = useDispatch();

    const activeAccount = useSelector((state) => state.subscriber.activeAccount);
    const user = useSelector((state) => state.theme.user);

    const [data, setData] = useState({});
    const [show, setShow] = useState(false);
    const [showToggle, setShowToggle] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [viewSchedule, setViewSchedule] = useState([]);
    useEffect(() => {
        if (!stepsData.id || activeStep !== 6) return;

        setIsLoading(true);
        callTokenApi(`${API_AI_INSIGHTS_TEMPLATE_SCHEDULES}/template/${stepsData.id}`, 'GET')
            .then((response) => {
                if (response.status === 200) {
                    const data = response.data.data;
                    setViewSchedule(data);
                } else {
                    toastError(response);
                }
            })
            .finally(() => setIsLoading(false));
    }, [stepsData.id, activeStep]);


    const handleSchedule = (id) => {
        
        const schedule = viewSchedule.find((item) => item.id === id);

        clearUnsavedChangesFlag();

        dispatch(
            setFlexibleMultiModal({
                show: true,
                component: COMPONENT_NAME.AI_INSIGHTS_TEMPLATE_SCHEDULE,
                attributes: {
                    id: stepsData.id,
                    dataSources: stepsData.dataSources,
                    schedule,
                    setViewSchedule,
                    setViewTemplates,
                    handleSetUnsavedChanges,
                }
            })
        );
    };

    const onSubmit = () => {
        if (!stepsData.id) return;

        if (!viewSchedule || viewSchedule.length === 0) {
            toast.error('At least one schedule is required before publishing the template.');
            return;
        }

        if (stepsData.status !== 'draft') {
            dispatch(
                setFlexibleMultiModal({
                    show: false,
                    component: COMPONENT_NAME.AI_INSIGHTS_TEMPLATE,
                })
            );

            return;
        };

        const currentStep = 6;
        const tplStatus = 'active';

        setSaveLoading(true);

        callTokenApi(`${API_AI_INSIGHTS_TEMPLATES_BY_ACCOUNT_CREATE}/${stepsData.id}`, 'PUT', {
            status: tplStatus,
            lastEditStep: stepsData.lastEditStep && stepsData.lastEditStep > currentStep
                ? stepsData.lastEditStep
                : currentStep,
        })
            .then((response) => {
                if (response.status === 200) {
                    toast.success(`Templates created successfully.`);

                    setViewTemplates((prev) => prev.map((tpl) => {
                        if (tpl.id === stepsData.id) {
                            return {
                                ...tpl,
                                status: tplStatus,
                            };
                        }

                        return tpl;
                    }));

                    if(tplStatus === "active") {
                        dispatch(fetchSavedReportsRequest(activeAccount.id, user.id)); 
                    }
                } else {
                    toastError(response);
                }
            })
            .catch((error) => {
                console.error('Update schedule error:', error);
                toast.error('Failed to update schedule. Please try again later.');
            })
            .finally(() => {
                if (typeof clearUnsavedChangesFlag === 'function') {
                    clearUnsavedChangesFlag(); // Clear flag only on FULL success
                }

                dispatch(
                    setFlexibleMultiModal({
                        show: false,
                        component: COMPONENT_NAME.AI_INSIGHTS_TEMPLATE,
                    })
                );
            });
    };

    const onDelete = (id) => {
        if (!id) return;

        setSaveLoading(true);

        callTokenApi(`${API_AI_INSIGHTS_TEMPLATE_SCHEDULES}/${id}`, 'DELETE')
            .then((response) => {
                if (response.status === 200) {
                    toast.success('Schedule deleted successfully.');
                    setViewSchedule((prev) => prev.filter((item) => item.id !== id));

                    const templateStatus = response.data?.templateStatus;

                    setViewTemplates((prev) => {
                        return prev.map((tpl) => {
                            if (tpl.id === stepsData.id) {
                                return {
                                    ...tpl,
                                    schedules: tpl.schedules.filter((schedule) => schedule.id !== id),
                                    ...(templateStatus ? { status: templateStatus } : {}),
                                };
                            }
                            return tpl;
                        });
                    });
                } else {
                    toastError(response);
                }
            })
            .catch((error) => {
                console.error('Delete schedule error:', error);
                toast.error('Failed to delete schedule. Please try again later.');
            })
            .finally(() => {
                setShow(false);
                setSaveLoading(false);
                handleSetUnsavedChanges(true);
            });
    }

    const onToggle = (id, status) => {
        if (!id) return;

        setSaveLoading(true);

        callTokenApi(`${API_AI_INSIGHTS_TEMPLATE_SCHEDULES}/${id}`, 'PUT', {
            status
        })
            .then((response) => {
                if (response.status === 200) {
                    toast.success(`Schedule ${status} updated successfully.`);

                    const responseData = response?.data?.data;

                    setViewSchedule((prev) => {
                        const existsIndex = prev.findIndex(tpl => tpl.id === responseData.id);

                        if (existsIndex !== -1) {
                            const updatedTemplates = [...prev];
                            updatedTemplates[existsIndex] = responseData;
                            return updatedTemplates;
                        }

                        return [responseData, ...prev];
                    });

                    setViewTemplates((prev) => {
                        return prev.map((tpl) => {
                            if (tpl.id === stepsData.id) {
                                return {
                                    ...tpl,
                                    schedules: tpl.schedules.map((schedule) => {
                                        if (schedule.id === id) {
                                            return {
                                                ...schedule,
                                                status,
                                            };
                                        }

                                        return schedule;
                                    })
                                };
                            }
                            return tpl;
                        });
                    });
                } else {
                    toastError(response);
                }
            })
            .catch((error) => {
                console.error('Update schedule error:', error);
                toast.error('Failed to update schedule. Please try again later.');
            })
            .finally(() => {
                setShowToggle(false);
                setSaveLoading(false);
                handleSetUnsavedChanges(true);
            });
    }

    return (
        <StepCardHeader
            title={`Schedule`}
            step={6}
            activeStep={activeStep}
            lastEditStep={stepsData.lastEditStep}
            setActiveStep={() => setActiveStep(6)}
        >
            <p>Now it's time to schedule your analysis to run on a recurring basis, for example daily, weekly, on the 3rd of every month, etc. When your analysis runs, the schedule should align well the data you have selected in step 2. For example, on the 3rd of each month, you may run this analysis and include a report containing the previous month's data. </p>

            <CRow>
                {(isLoading) ? (
                    <CCol lg="12">
                        <div className="loading text-center mt-4">
                            <CenterSpinner color="primary" />
                        </div>
                    </CCol>
                ) : (
                    <>
                        {viewSchedule.length > 0 && (
                            <CCol lg="12">
                                <div className="accordion list-custom-reports">
                                    <div className="accordion-item" style={{ border: 0 }}>
                                        <div className="list-rules">
                                            {viewSchedule.map((schedule, index) => (
                                                <ViewScheduleCard
                                                    key={index}
                                                    index={index}
                                                    viewReport={schedule}
                                                    onEdit={() => handleSchedule(schedule.id)}
                                                    onDelete={() => {
                                                        setShow(true);
                                                        setData(schedule);
                                                    }}
                                                    onStatus={() => {
                                                        setShowToggle(true);
                                                        setData(schedule);
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </CCol>
                        )}

                        <CCol lg="12">
                            <div className='d-flex items-center justify-content-between mb-4'>
                                <CButton
                                    type="button"
                                    onClick={() => handleSchedule()}
                                >
                                    <div className='d-flex align-items-center gap-2'>
                                        <CIcon icon="iconAddField" width={24} height={24} />
                                        <span>Add Schedule</span>
                                    </div>
                                </CButton>
                            </div>
                        </CCol>
                    </>
                )}
            </CRow>

            <CButton type="button" className="px-4" color="primary" onClick={onSubmit}>
                Save & Close
            </CButton>

            <ConfirmRemovePopup
                show={show}
                onAccept={() => onDelete(data.id)}
                onClose={() => {
                    setShow(false);
                    setData({});
                }}
                title='Delete this Schedule'
                isLoading={saveLoading}
            >
                <p>You are about to delete this Schedule. Please confirm.</p>
            </ConfirmRemovePopup>

            {showToggle && (
                <ConfirmSaveChange
                    show={showToggle}
                    onAccept={() => onToggle(data.id, data.status === 'active' ? 'inactive' : 'active')}
                    onClose={() => {
                        setShowToggle(false);
                        setData({});
                    }}
                    isLoading={saveLoading}
                    title="Are you sure?"
                >
                    <p>
                        You are about to {data?.status === 'active' ? 'inactive' : 'active'} the schedule{' '}
                        <strong>"{data ? data.name : ''}"</strong>.
                    </p>
                </ConfirmSaveChange>
            )}
        </StepCardHeader>
    );
};

export default Schedule;
